<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">
          <h6 class="element-header">
            {{ $t('dashboard.dashboard_for') + piniaUserStore().currentUser?.user?.userAccessRightsTranslation }}
          </h6>

          <div class="element-box mt-4">
            <div class="row mt-4">
              <CoachingButton
                  :enabled="companySettings?.privateCoachingEnabled"
                  route-name="privateCoachingNewAndActiveList"
                  coaching-type="private"
                  label="Einzelcoaching"/>
              <CoachingButton
                  :enabled="companySettings?.groupCoachingEnabled"
                  route-name="groupCoachingNewAndActiveList"
                  coaching-type="group"
                  label="Gruppenmaßnahmen"/>
              <CoachingButton
                  :enabled="companySettings?.socioTherapyCoachingEnabled"
                  route-name="socioTherapyCoachingNewAndActiveList"
                  coaching-type="socioTherapy"
                  label="Soziotherapie"/>
              <CoachingButton
                  :enabled="companySettings?.assistedLivingCoachingEnabled"
                  route-name="assistedLivingCoachingNewAndActiveList"
                  coaching-type="assistedLiving"
                  label="Betreutes Wohnen"/>
              <CoachingButton
                  :enabled="companySettings?.privateLearningCoachingEnabled"
                  route-name="privateLearningCoachingNewAndActiveList"
                  coaching-type="privateLearning"
                  label="Private Weiterbildung"/>
            </div>
          </div>

          <h6 class="mt-5">{{ $t('general.active_customers') }}</h6>
          <div class="row mt-3">
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'Einzelcoaching'" :value="infoData?.countActiveCoaching?.counts?.privateCoaching"/>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'Gruppenmaßnahmen'" :value="infoData?.countActiveCoaching?.counts?.groupCoaching"/>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'Soziotherapie'" :value="infoData?.countActiveCoaching?.counts?.socioTherapyCoaching"/>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'Private Weiterbildung'"
                        :value="countPrivateLearningCoaching"/>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'Betreutes Wohnen'"
                        :value="countAssistedLivingCoaching"/>
            </div>
          </div>

          <h6 class="mt-4">Übersicht Standorte</h6>
          <div class="row mt-3">
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'Anzahl interne Standorte'" :value="infoData?.countInternalOffices"/>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'Anzahl externe Standorte'" :value="infoData?.countExternalOffices"/>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'Anzahl sonstige'" :value="infoData?.countOtherOffices"/>
            </div>
          </div>
        </div>
      </div>
      <FormSidebar>
        <template #top>
          <h6 class="element-header">
            Sidebar
          </h6>
        </template>

        <div class="element-box">
          <h6>{{ $t('general.active_customers') }}</h6>
          <VueApexCharts width="100%" type="donut" :options="chartOptions" :series="seriesActiveCoach"/>
        </div>

        <div class="element-box">
          <h6>Standorte nach Bundesländern</h6>
          <div v-for="user of infoData?.countOfficeByFederalState" v-bind:key="user?.name"
               class="d-flex justify-content-between">
            <span>{{ user?.federalState }}</span>
            <span>{{ user?.count }}</span>
          </div>
        </div>

        <div class="element-box">
          <h6>{{ $t('general.birthdays') }}</h6>
          <div v-for="user of infoData?.todayBirthdays" v-bind:key="user?.name" class="d-flex justify-content-between">
            <span>{{ user?.fullName }}</span>
            <span>{{ user?.date }}</span>
          </div>
        </div>
      </FormSidebar>
    </div>
  </div>
</template>

<script setup>
import dashboardHeadMarketingService from '@/api/Dashboard/HeadMarketing';
import CardInfo from '@/components/card/CardInfo.vue';
import CoachingButton from '@/components/button/CoachingButton.vue';
import FormSidebar from '@/components/FormSidebar.vue';
import { ErrorToast } from '@/components/VueToast';
import { optionsChartDonut } from '@/shared/utils/apex';
import { piniaCompanySettingsStore } from '@/store/piniaCompanySettingsStore';
import { piniaUserStore } from '@/store/piniaUserStore';
import { computed, onMounted, ref } from 'vue';
import VueApexCharts from 'vue3-apexcharts';
import {initAppIfAuthenticated} from "@/shared/functions";

const piniaCompanySettingsState = piniaCompanySettingsStore();
const companySettings = computed(() => piniaCompanySettingsState.companySettings);

const chartOptions      = ref(optionsChartDonut);
const seriesActiveCoach = ref([]);

const infoData = ref({});

async function getInfoData() {
  try {
    const { data } = await dashboardHeadMarketingService.getInfoData();
    infoData.value = data.data;

    const countActiveCoachingTotal =
              data.data.countActiveCoaching?.total +
              infoData?.value?.countNewCoaching?.counts?.privateLearningCoaching +
              infoData?.value?.countNewCoaching?.counts?.assistedLivingCoaching;


    seriesActiveCoach.value = Object.values(data.data.countActiveCoaching?.counts);
    chartOptions.value      = {
      ...chartOptions.value,
      labels: [
        `Einzelcoaching ${data.data.countActiveCoaching.counts?.privateCoaching}`,
        `Gruppenmaßnahmen ${data.data.countActiveCoaching.counts?.groupCoaching}`,
        `Soziotherapie ${data.data.countActiveCoaching.counts?.socioTherapyCoaching}`,
        `Private Weiterbildung ${countPrivateLearningCoaching.value}`,
        `Betreutes Wohnen ${countAssistedLivingCoaching.value}`,
      ],
      plotOptions: {
        pie: {
          donut: {
            ...chartOptions.value.plotOptions.pie.donut,
            labels: {
              ...chartOptions.value.plotOptions.pie.donut.labels,
              total: {
                ...chartOptions.value.plotOptions.pie.donut.labels.total,
                formatter: () => countActiveCoachingTotal.toString(),
              },
            },
          },
        },
      },
    };
  } catch (error) {
    await ErrorToast(error);
  }
}


const countPrivateLearningCoaching = computed(() =>
    (infoData?.value?.countActiveCoaching?.counts?.privateLearningCoaching ?? 0) +
    (infoData?.value?.countNewCoaching?.counts?.privateLearningCoaching ?? 0),
);

const countAssistedLivingCoaching = computed(() =>
    (infoData?.value?.countActiveCoaching?.counts?.assistedLivingCoaching ?? 0) +
    (infoData?.value?.countNewCoaching?.counts?.assistedLivingCoaching ?? 0),
);


onMounted(async () => {
  await initAppIfAuthenticated()
  await getInfoData();
});
</script>

<style scoped>
::v-deep(.apexcharts-legend) {
  display: block !important;
}
</style>